import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { tx } from "@/i18n";

const FLAG_LABELS = { meter_photo: "Meterstand foto", review: "Review", card_pen: "Kaartje/pen", guestbook: "Gastenboek" };

function KeywordEditor({ type, categories, items, setItems }) {
  const [draft, setDraft] = useState({});
  const upd = (id, patch) => setItems((all) => ({ ...all, [type]: all[type].map((k) => (k.id === id ? { ...k, ...patch } : k)) }));
  const del = (id) => setItems((all) => ({ ...all, [type]: all[type].filter((k) => k.id !== id) }));
  const add = (cat) => {
    const label = (draft[cat] || "").trim();
    if (!label) return;
    setItems((all) => ({ ...all, [type]: [...all[type], { id: `new-${Date.now()}`, label, category: cat, flag: null }] }));
    setDraft((d) => ({ ...d, [cat]: "" }));
  };
  return (
    <div className="space-y-4">
      {categories.map((c, i) => (
        <div key={c.key} data-testid={`kw-cat-${type}-${c.key}`}>
          <p className="text-sm font-semibold mb-1.5">{i + 1}. {tx(c.label)}</p>
          <div className="flex flex-wrap gap-1.5">
            {items[type].filter((k) => k.category === c.key).map((k) => (
              <div key={k.id} className="flex items-center gap-1 rounded-full border border-border bg-card pl-2 pr-1 py-0.5">
                <input value={k.label} onChange={(e) => upd(k.id, { label: e.target.value })} className="bg-transparent text-sm w-[90px] outline-none" data-testid={`kw-label-${k.id}`} />
                <select value={k.category} onChange={(e) => upd(k.id, { category: e.target.value })} className="text-[11px] bg-transparent text-muted-foreground max-w-[28px]" title={tx("Verplaats naar categorie")} data-testid={`kw-cat-select-${k.id}`}>
                  {categories.map((cc, j) => <option key={cc.key} value={cc.key}>{`${j + 1}. ${tx(cc.label)}`}</option>)}
                </select>
                <button type="button" onClick={() => del(k.id)} className="text-muted-foreground hover:text-destructive" data-testid={`kw-delete-${k.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <div className="flex items-center gap-1">
              <Input value={draft[c.key] || ""} onChange={(e) => setDraft((d) => ({ ...d, [c.key]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && add(c.key)} placeholder={tx("Nieuw")} className="h-7 w-24 text-sm" data-testid={`kw-new-${type}-${c.key}`} />
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => add(c.key)} data-testid={`kw-add-${type}-${c.key}`}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function HouseFlags({ houses, setHouses }) {
  const toggle = async (h, key, v) => {
    const flags = { ...h.flags, [key]: v };
    setHouses((hs) => hs.map((x) => (x.id === h.id ? { ...x, flags } : x)));
    try { await api.put(`/report-settings/houses/${h.id}`, flags); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="report-house-flags">
        <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2 pr-2">{tx("Woning")}</th>{Object.keys(FLAG_LABELS).map((k) => <th key={k} className="py-2 px-1 text-center">{tx(FLAG_LABELS[k])}</th>)}</tr></thead>
        <tbody>
          {houses.map((h) => (
            <tr key={h.id} className="border-t border-border">
              <td className="py-2 pr-2 font-medium">{h.name}</td>
              {Object.keys(FLAG_LABELS).map((k) => (
                <td key={k} className="py-2 px-1 text-center"><Switch checked={!!h.flags[k]} onCheckedChange={(v) => toggle(h, k, v)} data-testid={`flag-${k}-${h.id}`} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReportSettingsDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState(null);
  const [houses, setHouses] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/report-settings").then(({ data }) => { setCfg(data); setItems(data.keywords); }).catch((e) => toast.error(apiError(e)));
    api.get("/report-settings/houses").then(({ data }) => setHouses(data)).catch(() => {});
  }, [open]);
  const strip = (arr) => arr.map((k) => ({ ...k, id: String(k.id).startsWith("new-") ? null : k.id }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/report-settings", { checkin: strip(items.checkin), checkout: strip(items.checkout) }); setItems(data.keywords); toast.success(tx("Steekwoorden opgeslagen")); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="report-settings-dialog">
        <DialogHeader><DialogTitle className="font-display">{tx("Rapportage-instellingen")}</DialogTitle><DialogDescription>{tx("Steekwoorden per afspraaktype en instellingen per woning.")}</DialogDescription></DialogHeader>
        {!cfg || !items ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <Tabs defaultValue="checkin">
            <TabsList><TabsTrigger value="checkin" data-testid="rs-tab-checkin">{tx("Incheck")}</TabsTrigger><TabsTrigger value="checkout" data-testid="rs-tab-checkout">{tx("Uitcheck")}</TabsTrigger><TabsTrigger value="houses" data-testid="rs-tab-houses">{tx("Per woning")}</TabsTrigger></TabsList>
            {["checkin", "checkout"].map((t) => (
              <TabsContent key={t} value={t} className="pt-3">
                <KeywordEditor type={t} categories={cfg.categories[t]} items={items} setItems={setItems} />
                <Button onClick={save} disabled={saving} className="mt-4 gap-1.5 bg-primary hover:bg-primary/90" data-testid={`rs-save-${t}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {tx("Opslaan")}</Button>
              </TabsContent>
            ))}
            <TabsContent value="houses" className="pt-3"><HouseFlags houses={houses} setHouses={setHouses} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
